import React, { useEffect, useRef } from 'react';
import { useAccessibility } from '../context/AccessibilityContext';
import { useLanguage } from '../context/LanguageContext';

interface SpeakableTextProps {
  children: React.ReactNode;
  text?: string; // Opțional: text custom pentru a fi citit
  autoSpeak?: boolean; // Citește automat când componenta devine vizibilă
  showSpeakButton?: boolean; // Afișează buton de speak
  className?: string;
  element?: React.ElementType; // Tipul elementului HTML (default: 'span')
}

const SpeakableText: React.FC<SpeakableTextProps> = ({
  children,
  text,
  autoSpeak = false,
  showSpeakButton = true,
  className = '',
  element = 'span'
}) => {
  const { settings, speakText, isSpeaking } = useAccessibility();
  const { t } = useLanguage();
  const elementRef = useRef<HTMLElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Text pentru citire
  const textToSpeak = text || (typeof children === 'string' ? children : 
    elementRef.current?.textContent || '');

  // Auto-speak când elementul devine vizibil
  useEffect(() => {
    if (!autoSpeak || !settings.textToSpeech || !textToSpeak) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
          speakText(textToSpeak);
          observerRef.current?.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (elementRef.current) {
      observerRef.current.observe(elementRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [autoSpeak, settings.textToSpeech, textToSpeak, speakText]);

  const handleSpeak = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (textToSpeak) {
      speakText(textToSpeak);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Spațiu sau Enter pentru a citi textul
    if ((e.key === ' ' || e.key === 'Enter') && e.altKey) {
      e.preventDefault();
      handleSpeak(e as any);
    }
  };

  // Dacă text-to-speech nu este activat și nu vrem să afișăm butonul, returnăm doar copiii
  if (!settings.textToSpeech && !showSpeakButton) {
    return React.createElement(element, { className, ref: elementRef }, children);
  }

  return React.createElement(
    element,
    {
      ref: elementRef,
      className: `speakable-text ${className} ${settings.textToSpeech ? 'tts-enabled' : ''}`,
      onKeyDown: handleKeyDown,
      tabIndex: settings.textToSpeech ? 0 : undefined,
      role: settings.textToSpeech ? 'button' : undefined,
      'aria-label': settings.textToSpeech ? `${textToSpeak} - ${t('accessibility.pressToSpeak')}` : undefined
    },
    children,
    
    // Buton de speak (vizibil doar la hover sau focus)
    showSpeakButton && settings.textToSpeech && textToSpeak && React.createElement(
      'button',
      {
        onClick: handleSpeak,
        className: `
          speak-button 
          ml-2 p-1 
          text-blue-600 hover:text-blue-800 
          opacity-0 group-hover:opacity-100 focus:opacity-100
          transition-opacity duration-200
          rounded
          focus:outline-none focus:ring-2 focus:ring-blue-500
        `,
        'aria-label': t('accessibility.speakThisText'),
        title: t('accessibility.speakThisText')
      },
      React.createElement(
        'svg',
        {
          className: 'w-4 h-4',
          fill: 'none',
          stroke: 'currentColor',
          viewBox: '0 0 24 24',
          'aria-hidden': 'true'
        },
        React.createElement('path', {
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          strokeWidth: 2,
          d: 'M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M9 12a3 3 0 103 3m3-3a3 3 0 10-3 3m3-3H6l-3 3 3 3h6'
        })
      )
    )
  );
};

export default SpeakableText; 